<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue';
import type { DatetimeFieldOptions, FilterInputEmits, FilterInputProps } from '#layers/data-model/public';
import { computed, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { DateTimePickerTriggerButton, pickerPopoverContent, pickerPopoverUi } from '#layers/ui/public';
import { buildDatetimeFilterContext, formatDatetimeFilterValue } from './filter-value';
import DateTimeFilterValueEditor from './FilterValueEditor.vue';

defineOptions({ name: 'DateTimeFilterInput' });

const props = defineProps<FilterInputProps<DatetimeFieldOptions>>();
const emit = defineEmits<FilterInputEmits>();

const { locale } = useI18n();

const context = computed(() => buildDatetimeFilterContext(props.primitiveType, props.typeOptions));

const displayValue = computed(() => formatDatetimeFilterValue(props.modelValue, context.value, locale.value));
const isInteractionDisabled = computed(() => props.disabled || props.readonly);
const popoverOpen = ref(false);
const triggerRef = useTemplateRef<ComponentPublicInstance>('triggerRef');

function focusFirstInput() {
	if (isInteractionDisabled.value) return;

	// The field-picker combobox restores focus to its trigger while it unmounts. Move focus
	// to this trigger first so that restoration cannot immediately dismiss the picker we open.
	(triggerRef.value?.$el as HTMLElement | undefined)?.focus();
	popoverOpen.value = true;
}

defineExpose({ focusFirstInput });

function updateModelValue(value: unknown) {
	if (isInteractionDisabled.value) return;

	emit('update:modelValue', value);
}
</script>

<template>
	<DPopover
		v-model:open="popoverOpen"
		:disabled="isInteractionDisabled"
		:content="{ ...pickerPopoverContent, align: 'start' }"
		:ui="pickerPopoverUi"
	>
		<DateTimePickerTriggerButton
			ref="triggerRef"
			:label="displayValue || '...'"
			:show-icon="false"
			:show-disclosure="false"
			:disabled
			:readonly
			:block="false"
			variant="ghost"
			size="xs"
			data-testid="datetime-filter-trigger"
			class="max-w-44"
		/>

		<template #content>
			<DateTimeFilterValueEditor
				:model-value
				:primitive-type
				:type-options
				size="sm"
				:disabled
				:readonly
				@update:model-value="updateModelValue"
			/>
		</template>
	</DPopover>
</template>
